import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';

import RegistrationForm from './components/RegistrationForm';
import MembershipPlansPanel from './components/MembershipPlansPanel';
import RecentRegistrations from './components/RecentRegistrations';

const ManualMemberRegistrationPortal = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, loading: authLoading, profileLoading } = useAuth();
  const [recentMembers, setRecentMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isStaffMember = isGymOwner || isStaff;

  useEffect(() => {
    // Wait for both auth and profile to load before checking permissions
    if (!authLoading && !profileLoading) {
      if (!user || !isStaffMember) {
        navigate('/login');
      }
    }
  }, [user, isStaffMember, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isStaffMember) {
      loadRecentRegistrations();
    }
  }, [currentTenant?.id, isStaffMember]);

  const loadRecentRegistrations = async () => {
    try {
      const { data, error } = await supabase?.from('tenant_members')?.select(`
          *,
          user_profiles(full_name, email, phone, avatar_url)
        `)?.eq('tenant_id', currentTenant?.id)?.eq('role', 'member')?.order('joined_at', { ascending: false })?.limit(10);

      if (error) throw error;
      setRecentMembers(data || []);
    } catch (err) {
      console.error('Error loading recent registrations:', err);
    }
  };

  const handleRegistrationSuccess = () => {
    setSuccessMessage('Member registered successfully!');
    loadRecentRegistrations();
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading registration portal...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isStaffMember) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-card rounded-lg p-8 text-center">
            <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              This feature is only available to gym staff and owners.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-[1440px] px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Manual Member Registration Portal
              </h1>
              <p className="text-muted-foreground">
                Add new members directly with comprehensive profile creation and membership assignment
              </p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-center gap-3">
            <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            <p className="text-success">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Registration Form - Takes 2 columns */}
          <div className="lg:col-span-2">
            <RegistrationForm
              tenantId={currentTenant?.id}
              onSuccess={handleRegistrationSuccess}
              onError={setError}
            />
          </div>

          {/* Sidebar - Takes 1 column */}
          <div className="lg:col-span-1 space-y-6">
            <MembershipPlansPanel />
            <RecentRegistrations members={recentMembers} />
          </div>
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default ManualMemberRegistrationPortal;